import React, { useEffect, useState } from 'react';
import Loading from '@components/Loading';
import {
  Card,
  Spin,
  Row,
  Col,
  Modal,
  Table,
  Button,
  Space,
  Typography,
  message,
  Form,
  Radio,
  DatePicker,
  Input
} from 'antd';
import {
  WarningOutlined,
  ThunderboltOutlined,
  StopOutlined,
  RetweetOutlined,
  RiseOutlined,
  AlertOutlined,
  CheckOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  FullscreenOutlined,
  FullscreenExitOutlined
} from '@ant-design/icons';
import {
  apiGetWarningStatisticsByType,
  apiGetWarningsByTypeAndDate,
  apiResolvedWarning
} from '@services/ManageDevices/DeviceServices';
import { showError, showSuccess } from '@components/notification';
import moment from 'moment';
import { usePermission } from '@contexts/Auth/UsePermission';
import InputUploadImage from '@components/Upload/Upload';
import dayjs from 'dayjs';

const { TextArea } = Input;

const WarningStatistics = () => {
  const { warning } = usePermission();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total_warnings: 0,
    resolved_warnings: 0,
    unresolved_warnings: 0,
    cannot_resolve_warnings: 0,
    demolition: 0,
    demolition_resolved: 0,
    demolition_unresolved: 0,
    demolition_cannot_resolve: 0,
    magnetic: 0,
    magnetic_resolved: 0,
    magnetic_unresolved: 0,
    magnetic_cannot_resolve: 0,
    device_offline: 0,
    device_offline_resolved: 0,
    device_offline_unresolved: 0,
    device_offline_cannot_resolve: 0,
    reverse_flow: 0,
    reverse_flow_resolved: 0,
    reverse_flow_unresolved: 0,
    reverse_flow_cannot_resolve: 0,
    leakage: 0,
    leakage_resolved: 0,
    leakage_unresolved: 0,
    leakage_cannot_resolve: 0,
    over_flow: 0,
    over_flow_resolved: 0,
    over_flow_unresolved: 0,
    over_flow_cannot_resolve: 0,
    abnormal_consumption: 0,
    abnormal_consumption_resolved: 0,
    abnormal_consumption_unresolved: 0,
    abnormal_consumption_cannot_resolve: 0,
    manual_meter_reading: 0,
    manual_meter_reading_resolved: 0,
    manual_meter_reading_unresolved: 0,
    manual_meter_reading_cannot_resolve: 0
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [warningList, setWarningList] = useState([]);
  const [currentWarningType, setCurrentWarningType] = useState('');

  // Resolution form modal states
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [resolutionModalVisible, setResolutionModalVisible] = useState(false);
  const [resolutionLoading, setResolutionLoading] = useState(false);
  const [currentWarning, setCurrentWarning] = useState(null);
  const [resolutionForm] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await apiGetWarningStatisticsByType({
        date: moment().format('YYYY-MM-DD')
      });
      if (res.data?.data) {
        const data = res.data.data;
        setStats({
          total_warnings: parseInt(data.total_warnings || 0),
          resolved_warnings: parseInt(data.resolved_warnings || 0),
          unresolved_warnings: parseInt(data.unresolved_warnings || 0),
          cannot_resolve_warnings: parseInt(data.cannot_resolve_warnings || 0),
          demolition: parseInt(data.demolition || 0),
          demolition_resolved: parseInt(data.demolition_resolved || 0),
          demolition_unresolved: parseInt(data.demolition_unresolved || 0),
          demolition_cannot_resolve: parseInt(data.demolition_cannot_resolve || 0),
          magnetic: parseInt(data.magnetic || 0),
          magnetic_resolved: parseInt(data.magnetic_resolved || 0),
          magnetic_unresolved: parseInt(data.magnetic_unresolved || 0),
          magnetic_cannot_resolve: parseInt(data.magnetic_cannot_resolve || 0),
          device_offline: parseInt(data.device_offline || 0),
          device_offline_resolved: parseInt(data.device_offline_resolved || 0),
          device_offline_unresolved: parseInt(data.device_offline_unresolved || 0),
          device_offline_cannot_resolve: parseInt(data.device_offline_cannot_resolve || 0),
          reverse_flow: parseInt(data.reverse_flow || 0),
          reverse_flow_resolved: parseInt(data.reverse_flow_resolved || 0),
          reverse_flow_unresolved: parseInt(data.reverse_flow_unresolved || 0),
          reverse_flow_cannot_resolve: parseInt(data.reverse_flow_cannot_resolve || 0),
          leakage: parseInt(data.leakage || 0),
          leakage_resolved: parseInt(data.leakage_resolved || 0),
          leakage_unresolved: parseInt(data.leakage_unresolved || 0),
          leakage_cannot_resolve: parseInt(data.leakage_cannot_resolve || 0),
          over_flow: parseInt(data.over_flow || 0),
          over_flow_resolved: parseInt(data.over_flow_resolved || 0),
          over_flow_unresolved: parseInt(data.over_flow_unresolved || 0),
          over_flow_cannot_resolve: parseInt(data.over_flow_cannot_resolve || 0),
          abnormal_consumption: parseInt(data.abnormal_consumption || 0),
          abnormal_consumption_resolved: parseInt(data.abnormal_consumption_resolved || 0),
          abnormal_consumption_unresolved: parseInt(data.abnormal_consumption_unresolved || 0),
          abnormal_consumption_cannot_resolve: parseInt(
            data.abnormal_consumption_cannot_resolve || 0
          ),
          manual_meter_reading: parseInt(data.manual_meter_reading || 0),
          manual_meter_reading_resolved: parseInt(data.manual_meter_reading_resolved || 0),
          manual_meter_reading_unresolved: parseInt(data.manual_meter_reading_unresolved || 0),
          manual_meter_reading_cannot_resolve: parseInt(
            data.manual_meter_reading_cannot_resolve || 0
          )
        });
      }
    } catch (error) {
      console.error('Error fetching warning statistics:', error);
      showError('Không thể tải thống kê cảnh báo');
    } finally {
      setLoading(false);
    }
  };

  const fetchWarningDetails = async (warningType, label) => {
    setModalLoading(true);
    setModalTitle(label);
    setCurrentWarningType(warningType);
    setModalVisible(true);

    try {
      const params = {
        page: 1,
        size: 100,
        date: moment().format('YYYY-MM-DD'),
        warningType: warningType
      };

      const res = await apiGetWarningsByTypeAndDate(params);
      setWarningList(res.data?.data || []);
    } catch (error) {
      console.error('Error fetching warning details:', error);
      showError('Không thể tải chi tiết cảnh báo');
      setWarningList([]);
    } finally {
      setModalLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const warningCards = [
    // Line 1
    {
      label: 'Cảnh báo mất kết nối',
      value: stats.device_offline,
      resolved: stats.device_offline_resolved,
      unresolved: stats.device_offline_unresolved,
      cannotResolve: stats.device_offline_cannot_resolve,
      type: 'DEVICE_OFFLINE',
      bgColor: '#fff4e6',
      textColor: '#d46b08'
    },
    {
      label: 'Cảnh báo cần đi đọc chỉ số thủ công',
      value: stats.manual_meter_reading,
      resolved: stats.manual_meter_reading_resolved,
      unresolved: stats.manual_meter_reading_unresolved,
      cannotResolve: stats.manual_meter_reading_cannot_resolve,
      type: 'MANUAL_METER_READING',
      bgColor: '#d6f0ff',
      textColor: '#0958d9'
    },
    {
      label: 'Cảnh báo tấn công tháo rời',
      value: stats.demolition,
      resolved: stats.demolition_resolved,
      unresolved: stats.demolition_unresolved,
      cannotResolve: stats.demolition_cannot_resolve,
      type: 'DEMOLITION',
      bgColor: '#ffe0e6',
      textColor: '#cf1322'
    },
    // Line 2
    {
      label: 'Cảnh báo tấn công từ tính',
      value: stats.magnetic,
      resolved: stats.magnetic_resolved,
      unresolved: stats.magnetic_unresolved,
      cannotResolve: stats.magnetic_cannot_resolve,
      type: 'MAGNETIC',
      bgColor: '#e6d7ff',
      textColor: '#531dab'
    },
    {
      label: 'Cảnh báo rò rỉ nước',
      value: stats.leakage,
      resolved: stats.leakage_resolved,
      unresolved: stats.leakage_unresolved,
      cannotResolve: stats.leakage_cannot_resolve,
      type: 'LEAKAGE',
      bgColor: '#e6f7e6',
      textColor: '#389e0d'
    },
    {
      label: 'Cảnh báo tiêu thụ bất thường',
      value: stats.abnormal_consumption,
      resolved: stats.abnormal_consumption_resolved,
      unresolved: stats.abnormal_consumption_unresolved,
      cannotResolve: stats.abnormal_consumption_cannot_resolve,
      type: 'ABNORMAL_CONSUMPTION',
      bgColor: '#fffbe6',
      textColor: '#d48806'
    }
  ];

  const handleResolved = (record) => {
    setCurrentWarning(record);
    resolutionForm.setFieldsValue({
      status: 1,
      resolved_at: dayjs(),
      resolved_image: null,
      remarks: ''
    });
    setResolutionModalVisible(true);
  };

  const handleResolutionSubmit = async () => {
    try {
      const values = await resolutionForm.validateFields();
      setResolutionLoading(true);

      // Prepare resolved_image - InputUploadImage returns a single file object or null
      const resolvedImage = values.resolved_image;
      const imageUrls = resolvedImage?.url ? [resolvedImage.url] : [];

      const payload = {
        status: values.status,
        resolved_at: values.resolved_at
          ? dayjs(values.resolved_at).format('YYYY-MM-DD HH:mm:ss')
          : null,
        resolved_image: JSON.stringify(imageUrls),
        remarks: values.remarks || ''
      };

      await apiResolvedWarning(currentWarning?.id, payload);
      showSuccess({ message: 'Cập nhật thành công!' });
      setResolutionModalVisible(false);
      resolutionForm.resetFields();
      fetchWarningDetails(currentWarningType, modalTitle);
      fetchData(); // Refresh statistics
    } catch (error) {
      console.error('Error resolving warning:', error);
      if (error?.errorFields) {
        showError('Vui lòng điền đầy đủ thông tin bắt buộc');
      } else {
        showError('Không thể cập nhật trạng thái');
      }
    } finally {
      setResolutionLoading(false);
    }
  };

  const columns = [
    {
      title: 'STT',
      dataIndex: 'stt',
      key: 'stt',
      width: 60,
      render: (text, record, index) => index + 1
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (text, record) => {
        const statusConfig = {
          0: { icon: CloseCircleFilled, color: '#ff4d4f', text: 'Chưa xử lý' },
          1: { icon: CheckCircleFilled, color: '#52c41a', text: 'Đã xử lý' },
          2: { icon: ExclamationCircleFilled, color: '#faad14', text: 'Không xử lý được' }
        };
        const config = statusConfig[record?.status] || statusConfig[0];
        const StatusIcon = config.icon;

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <StatusIcon style={{ fontSize: '20px', color: config.color }} />
            <Typography.Text
              style={{
                fontSize: '14px',
                color: config.color
              }}>
              {config.text}
            </Typography.Text>
          </div>
        );
      },
      width: 150
    },
    {
      title: 'Số Sêri',
      dataIndex: 'serial_no',
      key: 'serial_no',
      width: 100
    },
    {
      title: 'Khách hàng',
      dataIndex: 'customer_name',
      key: 'customer_name',
      render: (text, record) => (
        <>
          <span style={{ color: '#0051A9' }}>{record?.customer_id}</span> - {record?.customer_name}
        </>
      ),
      width: 200
    },
    {
      title: 'Nhà máy',
      dataIndex: 'branch',
      key: 'branch',
      width: 100
    },
    {
      title: 'TQL',
      dataIndex: 'manage_group',
      key: 'manage_group',
      width: 100
    },
    {
      title: 'ĐHT',
      dataIndex: 'parent_device',
      key: 'parent_device',
      width: 100
    },
    {
      title: 'Mạng lưới',
      dataIndex: 'route',
      key: 'route',
      width: 100
    },
    {
      title: 'Tên cảnh báo',
      dataIndex: 'name',
      key: 'name',
      width: 150
    },
    {
      title: 'Ngày xảy ra',
      dataIndex: 'date_check',
      key: 'date_check',
      render: (text, record) => (
        <>
          {record?.date_check
            ? moment(record?.date_check, 'HH:mm:ss DD/MM/YYYY').format('DD/MM/YYYY')
            : moment(record?.created_at)?.format('DD/MM/YYYY HH:mm')}
        </>
      ),
      width: 100
    },
    {
      title: 'Ngày thông báo',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text, record) => <>{moment(record?.created_at)?.format('DD/MM/YYYY HH:mm')}</>,
      width: 100
    },
    {
      title: 'Người xử lý',
      dataIndex: 'resolved_by',
      key: 'resolved_by',
      width: 100
    },
    {
      title: 'Thời điểm xử lý',
      dataIndex: 'resolved_at',
      key: 'resolved_at',
      render: (text, record) => (
        <>{record?.resolved_at ? moment(record?.resolved_at).format('DD/MM/YYYY HH:mm') : '-'}</>
      ),
      width: 120
    },
    {
      title: 'Ảnh xử lý',
      dataIndex: 'resolved_image',
      key: 'resolved_image',
      render: (text, record) => {
        // Parse resolved_image if it's a JSON string
        let images = record?.resolved_image;

        // If it's a string, try to parse it
        if (typeof images === 'string' && images.trim().startsWith('[')) {
          try {
            images = JSON.parse(images);
          } catch (e) {
            console.error('Error parsing resolved_image:', e);
            images = null;
          }
        }

        // Check if we have valid images array
        if (images && Array.isArray(images) && images.length > 0) {
          // If only 1 image, open directly in new tab
          if (images.length === 1) {
            return (
              <Button type="link" onClick={() => window.open(images[0], '_blank')}>
                Xem ảnh
              </Button>
            );
          }

          // If multiple images, show modal with clickable images
          return (
            <Button
              type="link"
              onClick={() => {
                Modal.info({
                  title: 'Ảnh xử lý (Click vào ảnh để mở tab mới)',
                  width: 600,
                  content: (
                    <div style={{ marginTop: 16 }}>
                      {images.map((url, index) => (
                        <img
                          key={index}
                          src={url}
                          alt={`Ảnh xử lý ${index + 1}`}
                          onClick={() => window.open(url, '_blank')}
                          style={{
                            width: '100%',
                            marginBottom: 8,
                            borderRadius: 0,
                            cursor: 'pointer',
                            transition: 'transform 0.2s'
                          }}
                          onMouseEnter={(e) => (e.target.style.transform = 'scale(1.02)')}
                          onMouseLeave={(e) => (e.target.style.transform = 'scale(1)')}
                        />
                      ))}
                    </div>
                  )
                });
              }}>
              Xem ảnh ({images.length})
            </Button>
          );
        }
        return '-';
      },
      width: 100
    },
    {
      title: 'Ghi chú xử lý',
      dataIndex: 'remarks',
      key: 'remarks',
      render: (text, record) => <>{record?.remarks || '-'}</>,
      width: 150
    },
    {
      title: 'Ghi chú',
      dataIndex: 'note',
      key: 'note',
      render: (text, record) => <>{record?.note}</>,
      width: 200
    },
    {
      title: 'Số ngày tồn tại',
      dataIndex: 'frequency',
      key: 'frequency',
      render: (text, record) => <>{record?.frequency || 1}</>,
      width: 200
    },
    ...(currentWarningType === 'MANUAL_METER_READING'
      ? [
          {
            title: 'Kỳ tham chiếu',
            dataIndex: 'period_reference',
            key: 'period_reference',
            render: (text, record) =>
              record?.period_reference ? moment(record.period_reference).format('MM/YYYY') : '-',
            width: 100
          }
        ]
      : []),
    {
      title: 'Hành động',
      dataIndex: 'actions',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          {warning?.updateStatus && record?.status == 0 && (
            <Button
              onClick={() => handleResolved(record)}
              icon={<CheckOutlined />}
              style={{
                backgroundColor: '#F4F5F7',
                color: '#0051A9',
                border: 'none'
              }}>
              Cập nhật trạng thái
            </Button>
          )}
        </Space>
      )
    }
  ];

  if (loading) {
    return (
      <div
        className="loading-container"
        style={{
          height: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        <Loading />
      </div>
    );
  }

  return (
    <div>
      <Row gutter={[16, 16]}>
        {warningCards.map((card, index) => (
          <Col xs={24} sm={12} md={8} key={index}>
            <Card
              style={{
                backgroundColor: card.bgColor,
                borderRadius: 0,
                border: 'none',
                height: '100%',
                minHeight: 230
              }}
              bodyStyle={{ padding: 16, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#000' }}>
                {card.label}
              </div>
              <div
                style={{
                  fontSize: 32,
                  fontWeight: 'bold',
                  color: card.textColor,
                  marginBottom: 8
                }}>
                {card.value === '--' ? '--' : card.value}
              </div>
              <div style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, color: '#666' }}>
                    <CheckCircleFilled style={{ color: '#52c41a', marginRight: 4 }} />
                    Đã xử lý:
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 500, color: '#52c41a' }}>
                    {card.resolved || 0}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, color: '#666' }}>
                    <CloseCircleFilled style={{ color: '#ff4d4f', marginRight: 4 }} />
                    Chưa xử lý:
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 500, color: '#ff4d4f' }}>
                    {card.unresolved || 0}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13, color: '#666' }}>
                    <ExclamationCircleFilled style={{ color: '#faad14', marginRight: 4 }} />
                    Không xử lý được:
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 500, color: '#faad14' }}>
                    {card.cannotResolve || 0}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <a
                  onClick={() => fetchWarningDetails(card.type, card.label)}
                  style={{ fontSize: 12, fontStyle: 'italic', color: '#1890ff' }}>
                  Chi tiết
                </a>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Modal
        title={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingRight: 32
            }}>
            <span>{modalTitle}</span>
            <Button
              type="text"
              icon={isFullScreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={() => setIsFullScreen((prev) => !prev)}
              title={isFullScreen ? 'Thu nhỏ' : 'Toàn màn hình'}
            />
          </div>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setIsFullScreen(false);
        }}
        maskClosable={false}
        width={isFullScreen ? '100vw' : 1000}
        style={isFullScreen ? { top: 0, padding: 0, maxWidth: '100vw' } : {}}
        footer={null}
        bodyStyle={
          isFullScreen
            ? { height: 'calc(100vh - 55px)', overflowY: 'auto', padding: '12px 16px' }
            : { maxHeight: '60vh', overflowY: 'auto' }
        }>
        {modalLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={warningList}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: false
            }}
            scroll={{ x: 'max-content', y: isFullScreen ? 'calc(100vh - 220px)' : 380 }}
          />
        )}
      </Modal>

      {/* Resolution Form Modal */}
      <Modal
        title="Cập nhật trạng thái xử lý cảnh báo"
        open={resolutionModalVisible}
        onCancel={() => {
          setResolutionModalVisible(false);
          resolutionForm.resetFields();
        }}
        maskClosable={false}
        onOk={handleResolutionSubmit}
        confirmLoading={resolutionLoading}
        okText="Cập nhật"
        cancelText="Hủy"
        width={600}>
        <Form form={resolutionForm} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item
            name="status"
            label="Trạng thái"
            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}>
            <Radio.Group>
              <Radio value={1}>Đã xử lý</Radio>
              <Radio value={2}>Không xử lý được</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="resolved_at"
            label="Thời điểm xử lý"
            rules={[{ required: true, message: 'Vui lòng chọn thời điểm xử lý' }]}>
            <DatePicker
              style={{ width: '100%' }}
              showTime={{ format: 'HH:mm:ss' }}
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="Chọn thời điểm"
            />
          </Form.Item>

          <Form.Item name="resolved_image" label="Ảnh xử lý">
            <InputUploadImage
              onChange={(file) => {
                resolutionForm.setFieldsValue({ resolved_image: file });
              }}
            />
          </Form.Item>

          <Form.Item name="remarks" label="Ghi chú">
            <TextArea
              rows={4}
              placeholder="Nhập ghi chú về việc xử lý cảnh báo"
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WarningStatistics;
